import { useState, useEffect } from 'react';
import Sidebar from '../Components/Sidebar';
import { Search, Trash2, RotateCcw, Eye, Calendar, User, Mail, Key, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../Intercepter/APiClient';

export default function DeletedUsersPage() {
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [restoreModal, setRestoreModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const itemsPerPage = 15;
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Fetch deleted users
  useEffect(() => {
    fetchDeletedUsers();
  }, []);

  const fetchDeletedUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v3/users/deleted');
      
      if (response.data.success) {
        const formattedUsers = response.data.users.map(user => ({
          id: user.id,
          regNo: user.registration_number,
          name: user.name,
          email: user.email,
          apiLimit: user.api_limit,
          apiUsed: user.api_used,
          deletedAt: user.deleted_at,
          deletedBy: user.deleted_by,
          reason: user.deletion_reason
        }));
        setDeletedUsers(formattedUsers);
        setError(null);
      } else {
        setError('Failed to fetch deleted users');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching deleted users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search
  useEffect(() => {
    const result = search
      ? deletedUsers.filter(u => 
          u.regNo.toLowerCase().includes(search.toLowerCase()) ||
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())
        )
      : deletedUsers;
    setFilteredUsers(result);
    setCurrentPage(1);
  }, [search, deletedUsers]);

  // Fetch user details
  const openUserDetails = async (user) => {
    try {
      setSelectedUser(user);
      setDetailsLoading(true);
      const response = await apiClient.get(`/api/v3/users/deleted/${user.id}`);
      
      if (response.data.success) {
        setUserDetails(response.data.user);
        setDetailsModal(true);
      } else {
        alert('Failed to fetch user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      alert('Error fetching user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Restore user
  const handleRestoreUser = async () => {
    try {
      setRestoring(true);
      const response = await apiClient.post('/api/v3/users/restore', {
        user_id: selectedUser.id
      });
      
      if (response.data.success) {
        setDeletedUsers(deletedUsers.filter(u => u.id !== selectedUser.id));
        setRestoreModal(false);
        setDetailsModal(false);
        alert('User restored successfully');
      } else {
        alert('Failed to restore user: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error restoring user:', err);
      alert('Error restoring user');
    } finally {
      setRestoring(false);
    }
  };

  // Permanently delete user
  const handlePermanentlyDeleteUser = async (user) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE: This will permanently delete ${user.name}'s account and all data. This cannot be undone. Are you absolutely sure?`)) {
      return;
    }
    
    try {
      const response = await apiClient.post('/api/v3/users/permanent-delete', {
        user_id: user.id
      });
      
      if (response.data.success) {
        setDeletedUsers(deletedUsers.filter(u => u.id !== user.id));
        alert('User permanently deleted');
      } else {
        alert('Failed to permanently delete user: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error permanently deleting user:', err);
      alert('Error permanently deleting user');
    }
  };

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin inline-block h-12 w-12 border-4 border-cyan-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-gray-300">Loading deleted users…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="text-white p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-red-500/20 p-3 rounded-lg">
                  <Trash2 size={32} className="text-red-400" />
                </div>
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                    Deleted Users
                  </h1>
                  <p className="text-gray-400 mt-1">Manage and recover deleted user accounts</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                <AlertCircle size={18} className="text-red-400" />
                <p className="text-red-200 text-sm">
                  Deleted users can be restored within 30 days. After that, accounts will be permanently deleted.
                </p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search size={20} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, registration number, or email..."
                  className="pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg w-full text-white placeholder:text-gray-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="text-red-400" size={20} />
                <p className="text-red-200">{error}</p>
              </div>
            )}

            {/* Users Table */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">User Info</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">API Usage</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Deleted Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Deletion Reason</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12">
                          <div className="flex flex-col items-center gap-3">
                            <AlertCircle size={48} className="text-gray-500 opacity-50" />
                            <p className="text-gray-400 text-lg">No deleted users found</p>
                            <p className="text-gray-500 text-sm">Users you delete will appear here</p>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedUsers.map(user => (
                      <tr key={user.id} className="hover:bg-white/5 transition-colors">
                        {/* User Info */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-lg p-2">
                              <User size={16} />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-white">{user.name}</div>
                              <div className="text-xs text-gray-400">{user.regNo}</div>
                            </div>
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="flex items-center gap-2">
                            <Mail size={14} className="text-gray-500" />
                            {user.email}
                          </div>
                        </td>

                        {/* API Usage */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm">
                            <span className="text-gray-300">{user.apiUsed.toLocaleString()}</span>
                            <span className="text-gray-500 mx-1">/</span>
                            <span className="text-gray-400">{user.apiLimit.toLocaleString()}</span>
                          </div>
                        </td>

                        {/* Deleted Date */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <Calendar size={14} className="text-gray-500" />
                            {new Date(user.deletedAt).toLocaleDateString()} {new Date(user.deletedAt).toLocaleTimeString()}
                          </div>
                        </td>

                        {/* Deletion Reason */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="bg-red-500/20 border border-red-500/30 text-red-300 px-3 py-1 rounded-full text-xs font-semibold inline-block max-w-xs truncate">
                            {user.reason || 'No reason provided'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => openUserDetails(user)}
                              className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-blue-300 hover:text-blue-200"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>

                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setRestoreModal(true);
                              }}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors text-green-300 hover:text-green-200"
                              title="Restore User"
                            >
                              <RotateCcw size={16} />
                            </button>

                            <button
                              onClick={() => handlePermanentlyDeleteUser(user)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-red-300 hover:text-red-200"
                              title="Permanently Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-white/10 flex justify-center">
                  <nav className="flex gap-2">
                    {[...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          currentPage === idx + 1 
                            ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                            : 'bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6">
                <p className="text-gray-400 text-sm mb-2">Total Deleted</p>
                <p className="text-3xl font-bold text-red-400">{deletedUsers.length}</p>
              </div>
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6">
                <p className="text-gray-400 text-sm mb-2">Recoverable</p>
                <p className="text-3xl font-bold text-yellow-400">
                  {deletedUsers.filter(u => {
                    const daysAgo = (Date.now() - new Date(u.deletedAt).getTime()) / (1000 * 60 * 60 * 24);
                    return daysAgo <= 30;
                  }).length}
                </p>
              </div>
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6">
                <p className="text-gray-400 text-sm mb-2">Total API Used</p>
                <p className="text-3xl font-bold text-cyan-400">{deletedUsers.reduce((sum, u) => sum + u.apiUsed, 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== MODALS ========== */}

      {/* User Details Modal */}
      {detailsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setDetailsModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full p-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-blue-500/20 p-3 rounded-lg">
                <Eye size={24} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">User Details</h2>
                <p className="text-gray-400 text-sm mt-1">Complete information about the deleted user</p>
              </div>
            </div>

            {detailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin text-cyan-400" size={32} />
              </div>
            ) : userDetails ? (
              <div className="space-y-6">
                {/* Personal Info */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Full Name</p>
                      <p className="text-white font-semibold">{userDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Registration Number</p>
                      <p className="text-white font-semibold">{userDetails.registration_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <p className="text-white font-semibold">{userDetails.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Status</p>
                      <p className="text-red-400 font-semibold">Deleted</p>
                    </div>
                  </div>
                </div>

                {/* API Info */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">API Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">API Limit</p>
                      <p className="text-white font-semibold">{userDetails.api_limit?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">API Used</p>
                      <p className="text-white font-semibold">{userDetails.api_used?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Remaining</p>
                      <p className="text-white font-semibold">{(userDetails.api_limit - userDetails.api_used)?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Usage %</p>
                      <p className="text-white font-semibold">{((userDetails.api_used / userDetails.api_limit) * 100).toFixed(2)}%</p>
                    </div>
                  </div>
                </div>

                {/* Deletion Info */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Deletion Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Deleted By</p>
                      <p className="text-white font-semibold">{userDetails.deleted_by || 'System'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Deleted At</p>
                      <p className="text-white font-semibold">{new Date(userDetails.deleted_at).toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-400 text-sm mb-1">Deletion Reason</p>
                      <p className="text-white font-semibold">{userDetails.deletion_reason || 'No reason provided'}</p>
                    </div>
                  </div>
                </div>

                {/* Days Until Permanent Deletion */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Days Until Permanent Deletion</p>
                  <p className="text-2xl font-bold text-yellow-400">
                    {Math.max(0, 30 - Math.floor((Date.now() - new Date(userDetails.deleted_at).getTime()) / (1000 * 60 * 60 * 24)))} days
                  </p>
                </div>
              </div>
            ) : null}

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setDetailsModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setDetailsModal(false);
                  setRestoreModal(true);
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg hover:shadow-green-500/50 text-white transition-all font-semibold"
              >
                Restore User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {restoreModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setRestoreModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-500/20 p-3 rounded-lg">
                <RotateCcw size={24} className="text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Restore User</h2>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
              <p className="text-green-200">
                Are you sure you want to restore <strong>{selectedUser?.name}</strong>? They will be able to access their account again.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setRestoreModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleRestoreUser}
                disabled={restoring}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg hover:shadow-green-500/50 text-white transition-all font-semibold disabled:opacity-50"
              >
                {restoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}